import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveFileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  ArrowLeft, Users as UsersIcon, KeyRound, Wallet, ShieldCheck, DollarSign,
  Mail, Phone, ChevronLeft, Copy, AlertCircle, BadgeCheck, GraduationCap,
  Upload, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const PHOTO_MAX_KB = 150;
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export default function ParentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credOpen, setCredOpen] = useState(false);
  const [creds, setCreds] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/parents/${id}/profile`);
      setProfile(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const resetPassword = async () => {
    try {
      const r = await api.post(`/parents/${id}/reset-password`);
      setCreds(r.data);
      setCredOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("photo_must_be_image")); return; }
    if (file.size > PHOTO_MAX_KB * 1024) {
      toast.error(t("photo_too_large", { kb: Math.ceil(file.size / 1024), max: PHOTO_MAX_KB }));
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/parents/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("photo_uploaded"));
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || d)) || t("error"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    if (!profile.photo_url) return;
    if (!window.confirm(t("parent_photo_delete_confirm"))) return;
    try {
      await api.delete(`/parents/${id}/photo`);
      toast.success(t("deleted_successfully"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("copied_to_clipboard"));
  };

  if (loading || !profile) {
    return <Card className="p-12 text-center text-muted-foreground">{t("loading")}…</Card>;
  }

  const initials = (profile.name || "").split(" ").slice(0, 2).map((p) => p[0]).join("");
  const totals = profile.totals || {};
  const avgAtt = totals.avg_attendance_pct;
  const attTone = avgAtt == null ? null : avgAtt >= 95 ? "emerald" : avgAtt >= 85 ? "blue" : avgAtt >= 70 ? "amber" : "rose";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/parents")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 me-1 rtl:rotate-180" /> {t("back_to_parents")}
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition text-sm" data-testid="parent-upload-photo-btn">
            <Upload className="w-4 h-4" />
            {uploadingPhoto ? t("uploading") + "…" : (profile.photo_url ? t("replace_photo") : t("choose_photo"))}
            <input type="file" accept={PHOTO_ACCEPT} onChange={onPickPhoto}
                   disabled={uploadingPhoto} className="hidden" data-testid="parent-photo-input" />
          </label>
          {profile.photo_url && (
            <Button variant="outline" size="sm" onClick={removePhoto} className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30" data-testid="parent-remove-photo-btn">
              <Trash2 className="w-4 h-4 me-1" /> {t("remove_photo")}
            </Button>
          )}
          <Button variant="outline" onClick={resetPassword} data-testid="reset-pwd-btn">
            <KeyRound className="w-4 h-4 me-1" /> {t("reset_password")}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <Card className="p-6 border shadow-none" data-testid="parent-hero">
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-24 h-24 rounded-2xl bg-primary/10 text-primary text-3xl font-bold grid place-items-center shrink-0 overflow-hidden" data-testid="parent-avatar">
            {profile.photo_url
              ? <img src={resolveFileUrl(profile.photo_url)} alt={profile.name} className="w-full h-full object-cover" />
              : (initials || <UsersIcon className="w-10 h-10" />)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-semibold truncate">{profile.name}</h1>
              {profile.active === false
                ? <Badge variant="destructive">{t("inactive")}</Badge>
                : <Badge className="bg-emerald-500 hover:bg-emerald-500">{t("active")}</Badge>}
              <Badge variant="secondary" className="text-xs">
                {t("role")}: {t(profile.role)}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {profile.email}</span>
              {profile.phone && (
                <span className="flex items-center gap-1.5 font-mono" dir="ltr">
                  <Phone className="w-3.5 h-3.5" /> {profile.phone}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full sm:w-auto sm:max-w-md">
            <Stat icon={UsersIcon} value={profile.children_count} label={t("children_count")} />
            <Stat
              icon={ShieldCheck}
              value={avgAtt == null ? "—" : `${avgAtt}%`}
              label={t("avg_attendance")}
              accent={attTone}
            />
            <Stat icon={Wallet} value={totals.wallet?.toFixed(2)} label={t("total_wallet_balance")} />
            <Stat
              icon={DollarSign}
              value={totals.fees_due?.toFixed(0)}
              label={t("total_fees_due")}
              accent={totals.fees_due > 0 ? "rose" : "emerald"}
            />
          </div>
        </div>
      </Card>

      <Tabs defaultValue="children">
        <TabsList>
          <TabsTrigger value="children" data-testid="tab-children"><UsersIcon className="w-4 h-4 me-1" /> {t("children")}</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account"><KeyRound className="w-4 h-4 me-1" /> {t("login_account")}</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact"><BadgeCheck className="w-4 h-4 me-1" /> {t("contact_info")}</TabsTrigger>
        </TabsList>

        <TabsContent value="children" className="mt-4">
          {profile.children.length === 0 ? (
            <Card className="p-12 text-center text-sm text-muted-foreground border-dashed">
              {t("no_linked_children")}
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="children-grid">
              {profile.children.map((c) => (
                <ChildCard key={c.id} child={c} onOpen={() => navigate(`/students/${c.id}`)} t={t} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="account" className="mt-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> {t("login_account")}</h3>
            <div className="space-y-2">
              <Row label={t("email")} value={profile.email} mono />
              <Row label={t("name")} value={profile.name} />
              <Row label={t("role")} value={t(profile.role)} />
              <Row label={t("status")} value={
                profile.active
                  ? <Badge className="bg-emerald-500 hover:bg-emerald-500">{t("active")}</Badge>
                  : <Badge variant="destructive">{t("inactive")}</Badge>
              } />
              {profile.last_login_at && (
                <Row label={t("last_login_at")} value={profile.last_login_at.slice(0, 19).replace("T", " ")} mono />
              )}
              {profile.created_at && (
                <Row label={t("created_at")} value={profile.created_at.slice(0, 10)} mono />
              )}
              <div className="pt-3">
                <Button variant="outline" onClick={resetPassword}>
                  <KeyRound className="w-4 h-4 me-1" /> {t("reset_password")}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="mt-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-primary" /> {t("contact_info")}</h3>
            <div className="space-y-2">
              <Row label={t("name")} value={profile.name} />
              <Row label={t("email")} value={profile.email} mono />
              <Row label={t("phone")} value={profile.phone} mono />
              <Row label={t("school")} value={profile.school_id} mono />
            </div>
            <div className="mt-4 text-xs text-muted-foreground bg-muted/30 p-3 rounded-md">
              💡 {t("contact_edit_hint")}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Credentials dialog */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" /> {t("password_reset")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200">
              <AlertCircle className="w-4 h-4 inline me-1" />
              {creds?.reset_email_sent
                ? `تم إرسال رابط تعيين كلمة المرور إلى ${creds?.email} (صالح 24 ساعة).`
                : `تعذّر إرسال البريد إلى ${creds?.email}. تحقق من إعدادات البريد الإلكتروني.`}
            </div>
            <div className="space-y-2">
              <Label>{t("email")}</Label>
              <div className="flex gap-2">
                <Input value={creds?.email || ""} readOnly className="font-mono" />
                <Button size="icon" variant="outline" onClick={() => copyToClipboard(creds?.email)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredOpen(false)}>{t("close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChildCard({ child, onOpen, t }) {
  const att = child.attendance_pct;
  const attTone = att == null ? "" : att >= 95 ? "text-emerald-600" : att >= 85 ? "text-blue-600" : att >= 70 ? "text-amber-600" : "text-rose-600";
  const initials = (child.full_name_ar || child.full_name || "")
    .split(" ").slice(0, 2).map((p) => p[0]).join("");
  return (
    <Card
      className="p-4 border shadow-none cursor-pointer hover:border-primary/40 hover:shadow-md transition"
      onClick={onOpen}
      data-testid={`child-card-${child.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center font-bold shrink-0 overflow-hidden">
          {initials || <GraduationCap className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{child.full_name_ar || child.full_name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {child.grade_name || "—"} · {child.class_name || "—"}
          </div>
        </div>
        <ChevronLeft className="w-4 h-4 text-muted-foreground rtl:rotate-180 shrink-0" />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        <Mini icon={ShieldCheck} value={att == null ? "—" : `${att}%`} label={t("attendance")} tone={attTone} />
        <Mini icon={Wallet} value={child.wallet_balance.toFixed(0)} label={t("wallet")} />
        <Mini
          icon={DollarSign}
          value={child.fees_due.toFixed(0)}
          label={t("fees")}
          tone={child.fees_due > 0 ? "text-rose-600" : ""}
        />
      </div>
    </Card>
  );
}

function Mini({ icon: Icon, value, label, tone }) {
  return (
    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-muted/30">
      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      <div className={`font-bold text-sm ${tone || ""}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Stat({ icon: Icon, value, label, accent }) {
  const tone = accent === "rose" ? "text-rose-600"
    : accent === "emerald" ? "text-emerald-600"
    : accent === "amber" ? "text-amber-600"
    : accent === "blue" ? "text-blue-600"
    : "text-foreground";
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
      <Icon className="w-5 h-5 text-primary shrink-0" />
      <div>
        <div className={`text-xl font-bold leading-none ${tone}`}>{value ?? "—"}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between text-sm gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value || "—"}</span>
    </div>
  );
}
